import { defineStore } from "pinia";
import type {
  SettingsState,
  Locale,
  Theme,
  Riwaya,
  AudioSettings,
  UserPreferences,
  PlayerFontSize,
  TassmiEndFeedback,
  RiwayaCatalog,
  SubscriptionChoice,
} from "../types";
import { settingsService } from "@/services/settingsService";
import { quranService } from "@/services/quranService";
import { AUTH_TOKEN } from "@/services/apiClient";
import riwayatFallback from "@/assets/quranpedia/riwayat.json";

const SETTINGS_KEY = "quran-settings";

// Default settings
const defaultAudioSettings: AudioSettings = {
  autoPlayNextAya: false,
  playbackSpeed: 1.0,
  volume: 1.0,
};

const defaultPreferences: UserPreferences = {
  showTranslation: true,
  showTransliteration: false,
  playerFontSize: "medium",
  tassmi3EndFeedback: "both",
  tassmi3Reciter: 263,
};

function defaultSettings(): SettingsState {
  return {
    locale: "ar",
    theme: "light",
    riwaya: "warsh",
    hasOnboarded: false,
    subscriptionChoice: "undecided",
    subscriptionRemindAt: null,
    audioSettings: { ...defaultAudioSettings },
    preferences: { ...defaultPreferences },
  };
}

// A device that already has an auth token or the legacy raw `locale` key
// (written by the pre-onboarding SelectLanguage.vue on every language pick)
// has definitely used the app before onboarding shipped — never show it
// the onboarding carousel on upgrade.
function looksLikeExistingInstall(): boolean {
  return (
    localStorage.getItem("locale") != null ||
    localStorage.getItem(AUTH_TOKEN) != null
  );
}

// Pre-onboarding screens wrote the chosen language only to this raw key,
// never through this store — prefer it so upgrading users keep their locale.
function legacyLocale(): Locale | null {
  const value = localStorage.getItem("locale");
  return value === "en" || value === "ar" ? (value as Locale) : null;
}

// Backend riwaya ids are lowercase; older saves may still hold "Hafs".
function normalizeRiwaya(value: unknown): Riwaya | null {
  return typeof value === "string" ? (value.toLowerCase() as Riwaya) : null;
}

interface SettingsStoreState extends SettingsState {
  riwayat: RiwayaCatalog;
}

function toSettingsSnapshot(state: SettingsStoreState): SettingsState {
  return {
    locale: state.locale,
    theme: state.theme,
    riwaya: state.riwaya,
    hasOnboarded: state.hasOnboarded,
    subscriptionChoice: state.subscriptionChoice,
    subscriptionRemindAt: state.subscriptionRemindAt,
    audioSettings: state.audioSettings,
    preferences: state.preferences,
  };
}

export const useSettingsStore = defineStore("settings", {
  state: (): SettingsStoreState => {
    let base: SettingsState;

    // Load from localStorage if exists
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        parsed.riwaya = normalizeRiwaya(parsed.riwaya);
        // Settings blob predates the onboarding feature — this device has
        // a history, never show it onboarding.
        if (typeof parsed.hasOnboarded !== "boolean") {
          parsed.hasOnboarded = true;
        }
        // Same story for the subscription gate: grandfather pre-feature devices.
        if (typeof parsed.subscriptionChoice !== "string") {
          parsed.subscriptionChoice = "free";
          parsed.subscriptionRemindAt = null;
        }
        base = parsed;
      } catch (e) {
        console.error("Failed to parse settings:", e);
        base = defaultSettings();
      }
    } else {
      base = defaultSettings();
      if (looksLikeExistingInstall()) {
        base.hasOnboarded = true;
      }
    }

    base.locale = legacyLocale() ?? base.locale;

    return { ...base, riwayat: riwayatFallback as RiwayaCatalog };
  },

  getters: {
    /**
     * Get current locale
     */
    currentLocale: (state): Locale => state.locale,

    /**
     * Get current theme
     */
    currentTheme: (state): Theme => state.theme,

    /**
     * Check if dark mode is enabled
     */
    isDarkMode: (state): boolean => state.theme === "dark",

    /**
     * Check if Arabic is selected
     */
    isArabic: (state): boolean => state.locale === "ar",

    /**
     * Get current riwaya (recitation tradition), or null if not picked yet
     */
    currentRiwaya: (state): Riwaya | null => state.riwaya,

    /** Undecided, or a past "remind later" has come due. */
    shouldPromptSubscription: (state): boolean =>
      state.subscriptionChoice === "undecided" ||
      (state.subscriptionChoice === "remind" &&
        state.subscriptionRemindAt != null &&
        Date.now() >= new Date(state.subscriptionRemindAt).getTime()),
  },

  actions: {
    /**
     * Set locale
     */
    setLocale(locale: Locale) {
      this.locale = locale;
      this.persist();
    },

    /**
     * Toggle locale
     */
    toggleLocale() {
      this.locale = this.locale === "ar" ? "en" : "ar";
      this.persist();
    },

    /**
     * Set theme
     */
    setTheme(theme: Theme) {
      this.theme = theme;
      this.persist();
    },

    /**
     * Set riwaya (recitation tradition)
     */
    setRiwaya(riwaya: Riwaya) {
      this.riwaya = riwaya;
      this.persist();
    },

    /**
     * Toggle theme
     */
    toggleTheme() {
      this.theme = this.theme === "light" ? "dark" : "light";
      this.persist();
    },

    /**
     * Update audio settings
     */
    updateAudioSettings(settings: Partial<AudioSettings>) {
      this.audioSettings = {
        ...this.audioSettings,
        ...settings,
      };
      this.persist();
    },

    /**
     * Update preferences
     */
    updatePreferences(preferences: Partial<UserPreferences>) {
      this.preferences = {
        ...this.preferences,
        ...preferences,
      };
      this.persist();
    },

    /**
     * Set player font size
     */
    setPlayerFontSize(size: PlayerFontSize) {
      this.preferences.playerFontSize = size;
      this.persist();
    },

    /**
     * Set tassmi' end-of-session feedback type
     */
    setTassmi3EndFeedback(type: TassmiEndFeedback) {
      this.preferences.tassmi3EndFeedback = type;
      this.persist();
    },

    /**
     * Set Warsh reciter for Mushaf audio playback
     */
    setTassmi3Reciter(id: number) {
      this.preferences.tassmi3Reciter = id;
      this.persist();
    },

    /**
     * Mark the first-run onboarding carousel as completed. Never shown again.
     */
    completeOnboarding() {
      this.hasOnboarded = true;
      this.persist();
    },

    setSubscriptionChoice(choice: SubscriptionChoice, remindAt: string | null = null) {
      this.subscriptionChoice = choice;
      this.subscriptionRemindAt = remindAt;
      this.persist();
    },

    /** Uses `created` from verify-otp, not device heuristics, so a reinstalled long-time account isn't re-prompted. */
    resolveSubscriptionGrandfathering(created: boolean) {
      if (!created && this.subscriptionChoice === "undecided") {
        this.setSubscriptionChoice("free");
      }
    },

    /** Also runs on token-expiry, not just logout — hasOnboarded/subscription/locale/theme/riwaya survive it. */
    resetToDefaults() {
      const { hasOnboarded, subscriptionChoice, subscriptionRemindAt, locale, theme, riwaya } = this;
      Object.assign(this, defaultSettings());
      this.hasOnboarded = hasOnboarded;
      this.subscriptionChoice = subscriptionChoice;
      this.subscriptionRemindAt = subscriptionRemindAt;
      this.locale = locale;
      this.theme = theme;
      this.riwaya = riwaya;
      this.persist();
    },

    persist() {
      const snapshot = toSettingsSnapshot(this);

      try {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(snapshot));
      } catch (e) {
        console.error("Failed to save settings:", e);
      }

      if (localStorage.getItem(AUTH_TOKEN)) {
        settingsService.saveSettings(snapshot).catch((e) => {
          console.error("Failed to sync settings to backend:", e);
        });
      }
    },

    async syncFromBackend() {
      try {
        const remote = await settingsService.getSettings();
        if (Object.keys(remote).length > 0) {
          Object.assign(this, remote);
          this.riwaya = normalizeRiwaya(this.riwaya);
          localStorage.setItem(
            SETTINGS_KEY,
            JSON.stringify(toSettingsSnapshot(this)),
          );
        } else {
          this.persist();
        }
      } catch (e) {
        console.error("Failed to sync settings from backend:", e);
      }
    },

    async fetchRiwayat() {
      try {
        this.riwayat = await quranService.getRiwayat();
      } catch (e) {
        console.error("Failed to fetch riwaya catalog:", e);
      }
    },
  },
});
